import { FC, ReactElement, useMemo } from "react";
import {
  DatePickerProps,
  DatePicker as MUIDatepicker,
} from "@mui/x-date-pickers/DatePicker";
import { Box, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LuCalendarDays } from "react-icons/lu";

type CustomDatePickerProps = {
  label?: string;
  value: Date | string | null;
  onChangeValue: (date: string | null) => void;
  required?: boolean;
  slotProps?: DatePickerProps<Date>["slotProps"];
} & Omit<DatePickerProps<Date>, "onChange" | "value">;

const DatePicker: FC<CustomDatePickerProps> = ({
  label,
  value,
  onChangeValue,
  required,
  slotProps,
  ...rest
}): ReactElement => {
  // Convert string date to Date object if needed
  const dateValue = useMemo(() => {
    if (!value) return null;
    if (value instanceof Date) return value;
    // If the value is a date-only string (YYYY-MM-DD), anchor to LOCAL midnight to display the same calendar day
    if (typeof value === "string" && value.length === 10 && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [yStr, mStr, dStr] = value.split("-");
      const y = Number(yStr);
      const m = Number(mStr);
      const d = Number(dStr);
      if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return null;
      if (m < 1 || m > 12 || d < 1 || d > 31) return null;
      const localDate = new Date(y, m - 1, d);
      // Ensure exact match (guards against invalid dates like 2024-02-31)
      if (
        localDate.getFullYear() !== y ||
        localDate.getMonth() !== m - 1 ||
        localDate.getDate() !== d
      ) {
        return null;
      }
      return localDate;
    }
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }, [value]);

  const handleChange = (newValue: Date | null) => {
    // Emit date-only string in YYYY-MM-DD using LOCAL components to keep the exact calendar day
    onChangeValue(
      newValue
        ? [
            String(newValue.getFullYear()),
            String(newValue.getMonth() + 1).padStart(2, "0"),
            String(newValue.getDate()).padStart(2, "0"),
          ].join("-")
        : null
    );
  };

  return (
    <Box sx={{ height: "100%", width: "100%" }}>
      {label && (
        <Typography
          sx={{ mb: 1, display: "flex", alignItems: "center", gap: 0.5 }}
          variant="subtitle2"
        >
          {label} {required && <Typography color="error">*</Typography>}
        </Typography>
      )}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <MUIDatepicker
          onChange={handleChange}
          value={dateValue}
          label={label}
          sx={{
            width: "100%",
            "& .MuiIconButton-root": {
              color: "#1A1A1A",
              fontSize: 20,
            },
            "& .MuiOutlinedInput-root": {
              border: "1px solid #e6e6e9",
              boxShadow: "none",
            },
            "& fieldset": {
              borderColor: "#e6e6e9",
            },
            "& input::placeholder": {
              color: "#1A1A1A",
              opacity: 1,
            },
          }}
          slotProps={{
            ...slotProps,
            textField: {
              ...slotProps?.textField,
              required,
            },
          }}
          {...rest}
          format="MM/dd/yyyy"
          slots={{
            openPickerIcon: LuCalendarDays,
          }}
        />
      </LocalizationProvider>
    </Box>
  );
};

export default DatePicker;
